import React, { useEffect, useMemo, useState } from 'react';
import { getMemberAccessToken } from '../auth/supabase';
import { personNamesMatch } from '../tracking/deploymentUsageModel';
import './availability.css';

const AVAILABILITY_SHEET_ID = '1-a4TOlei-7OkREMwq7W_tFbPAKUVwiwGcgeoevLXJTg';
const CALENDAR_SHEET = 'Availability Calendar';

function clean(value) {
  return String(value ?? '').trim();
}

function parseGviz(text) {
  const start = text.indexOf('{');
  const end = text.lastIndexOf('}') + 1;
  if (start < 0 || end <= start) throw new Error('Availability calendar returned an invalid response.');
  return JSON.parse(text.slice(start, end));
}

function cellValue(cell) {
  if (!cell) return '';
  if (cell.f !== undefined && cell.f !== null) return cell.f;
  return cell.v ?? '';
}

function localDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseCalendarDate(value) {
  const text = clean(value);
  if (!text) return null;

  const gviz = /^Date\((\d+),(\d+),(\d+)\)$/.exec(text);
  if (gviz) return new Date(Number(gviz[1]), Number(gviz[2]), Number(gviz[3]), 12, 0, 0, 0);

  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate(), 12, 0, 0, 0);
  return null;
}

function monthStart(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1, 12, 0, 0, 0);
}

function addMonths(date, amount) {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1, 12, 0, 0, 0);
}

function formatRange(start, end) {
  if (!start) return '';
  const startDate = new Date(`${start}T12:00:00`);
  const endDate = new Date(`${end || start}T12:00:00`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return '';
  const opts = { month: 'short', day: 'numeric', year: 'numeric' };
  if (start === end || !end) return startDate.toLocaleDateString([], opts);
  return `${startDate.toLocaleDateString([], opts)} – ${endDate.toLocaleDateString([], opts)}`;
}

function buildMonthDays(month, availabilityMap) {
  const first = monthStart(month);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  const days = [];
  for (let index = 0; index < 42; index += 1) {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    const key = localDateKey(date);
    days.push({
      key,
      date,
      inMonth: date.getMonth() === month.getMonth(),
      available: availabilityMap.get(key) === true,
      known: availabilityMap.has(key),
    });
  }
  return days;
}

export default function MyAvailability({ profile }) {
  const memberName = clean(profile?.full_name);
  const [calendar, setCalendar] = useState({ status: 'loading', dates: [], available: new Map(), matchedName: '', error: '' });
  const [month, setMonth] = useState(() => monthStart(new Date()));
  const [form, setForm] = useState({ startDate: '', endDate: '', removeAvailability: false });
  const [submitState, setSubmitState] = useState({ status: 'idle', message: '' });
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const goToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    goToTop();
    const frame = window.requestAnimationFrame(goToTop);
    return () => window.cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const url = `https://docs.google.com/spreadsheets/d/${AVAILABILITY_SHEET_ID}/gviz/tq?tqx=out:json&sheet=${encodeURIComponent(CALENDAR_SHEET)}`;

    setCalendar(current => ({ ...current, status: 'loading', error: '' }));
    fetch(url, { cache: 'no-store' })
      .then(async response => {
        if (!response.ok) throw new Error(`Availability calendar could not be loaded (${response.status}).`);
        return parseGviz(await response.text());
      })
      .then(json => {
        const columns = Array.isArray(json?.table?.cols) ? json.table.cols : [];
        const rows = Array.isArray(json?.table?.rows) ? json.table.rows : [];
        const memberRow = rows.find(row => personNamesMatch(cellValue(row?.c?.[0]), memberName));
        const matchedName = clean(cellValue(memberRow?.c?.[0]));

        const dates = [];
        const available = new Map();
        for (let index = 1; index < columns.length; index += 1) {
          const rawHeader = columns[index]?.label || columns[index]?.id || '';
          let date = parseCalendarDate(rawHeader);
          if (!date && index > 0) {
            const fallback = new Date();
            fallback.setHours(12, 0, 0, 0);
            fallback.setDate(fallback.getDate() + index - 1);
            date = fallback;
          }
          if (!date) continue;
          const key = localDateKey(date);
          const status = clean(cellValue(memberRow?.c?.[index])).toLowerCase();
          dates.push(date);
          available.set(key, status === 'yes');
        }

        if (!cancelled) setCalendar({
          status: 'ready',
          dates,
          available,
          matchedName,
          error: memberRow ? '' : 'Your approved member name was not found on the Line Medic availability calendar.',
        });
      })
      .catch(error => {
        if (!cancelled) setCalendar({ status: 'error', dates: [], available: new Map(), matchedName: '', error: error?.message || 'Unable to load availability calendar.' });
      });

    return () => { cancelled = true; };
  }, [memberName, refreshKey]);

  const monthDays = useMemo(() => buildMonthDays(month, calendar.available), [month, calendar.available]);
  const knownDates = useMemo(() => [...calendar.available.keys()].sort(), [calendar.available]);
  const availableDates = useMemo(() => knownDates.filter(key => calendar.available.get(key)), [knownDates, calendar.available]);

  const submit = async event => {
    event.preventDefault();
    const startDate = clean(form.startDate);
    const endDate = clean(form.endDate) || startDate;
    if (!startDate) return setSubmitState({ status: 'error', message: 'Choose a start date.' });
    if (endDate < startDate) return setSubmitState({ status: 'error', message: 'End date cannot be before start date.' });

    const verb = form.removeAvailability ? 'remove availability for' : 'add availability for';
    if (!window.confirm(`Confirm ${verb} ${formatRange(startDate, endDate)}?`)) return;

    setSubmitState({ status: 'saving', message: '' });
    try {
      const token = await getMemberAccessToken();
      if (!token) throw new Error('Member sign in required.');
      const response = await fetch('/api/member-availability', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ startDate, endDate, removeAvailability: Boolean(form.removeAvailability) }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Unable to submit availability.');

      setSubmitState({
        status: 'success',
        message: `${form.removeAvailability ? 'Availability removal' : 'Availability'} submitted for ${formatRange(startDate, endDate)}. The program calendar will update after the existing Google Sheet sync runs.`,
      });
      setForm({ startDate: '', endDate: '', removeAvailability: false });
      window.setTimeout(() => setRefreshKey(value => value + 1), 4000);
    } catch (error) {
      setSubmitState({ status: 'error', message: error?.message || 'Unable to submit availability.' });
    }
  };

  const todayKey = localDateKey(new Date());
  const firstKnown = knownDates[0] || '';
  const lastKnown = knownDates.at(-1) || '';

  return (
    <div className="availability-root my-availability-root">
      <section className="my-availability-identity">
        <div>
          <span className="availability-kicker">SIGNED-IN MEMBER</span>
          <h3>{memberName || 'Member'}</h3>
          <p>Availability submissions are locked to your approved account. There is no member selector.</p>
        </div>
        <span className="my-availability-lock">Identity verified</span>
      </section>

      {calendar.error && <div className="availability-note availability-note--warning">{calendar.error}</div>}
      {submitState.message && <div className={`availability-note availability-note--${submitState.status === 'error' ? 'warning' : 'success'}`}>{submitState.message}</div>}

      <div className="my-availability-layout">
        <section className="my-availability-calendar-card">
          <div className="my-availability-calendar-head">
            <div>
              <span className="availability-kicker">MY CALENDAR</span>
              <h3>{month.toLocaleDateString([], { month: 'long', year: 'numeric' })}</h3>
            </div>
            <div className="my-availability-calendar-nav">
              <button type="button" onClick={() => setMonth(current => addMonths(current, -1))} aria-label="Previous month">‹</button>
              <button type="button" onClick={() => setMonth(monthStart(new Date()))}>Today</button>
              <button type="button" onClick={() => setMonth(current => addMonths(current, 1))} aria-label="Next month">›</button>
            </div>
          </div>

          <div className="my-availability-weekdays" aria-hidden="true">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <span key={day}>{day}</span>)}
          </div>
          <div className="my-availability-calendar-grid">
            {monthDays.map(day => (
              <div
                key={day.key}
                className={[
                  'my-availability-day',
                  day.inMonth ? '' : 'is-outside',
                  day.key === todayKey ? 'is-today' : '',
                  day.available ? 'is-available' : '',
                  day.known && !day.available ? 'is-unavailable' : '',
                ].filter(Boolean).join(' ')}
                title={day.known ? (day.available ? 'Available' : 'Not available') : 'Outside current program calendar range'}
              >
                <span>{day.date.getDate()}</span>
                {day.available && <small>Available</small>}
              </div>
            ))}
          </div>
          <div className="my-availability-legend">
            <span><i className="legend-dot legend-dot--available" /> Available</span>
            <span><i className="legend-dot legend-dot--unavailable" /> Not available</span>
            {firstKnown && lastKnown && <small>Program calendar: {formatRange(firstKnown, lastKnown)}</small>}
          </div>
        </section>

        <section className="my-availability-form-card">
          <span className="availability-kicker">UPDATE AVAILABILITY</span>
          <h3>{form.removeAvailability ? 'Remove availability' : 'Add availability'}</h3>
          <p>Add or remove the dates you’re available for Line Medic deployments.</p>

          <div className="my-availability-mode">
            <button type="button" className={!form.removeAvailability ? 'is-active' : ''} onClick={() => setForm(current => ({ ...current, removeAvailability: false }))}>Add</button>
            <button type="button" className={form.removeAvailability ? 'is-active is-remove' : ''} onClick={() => setForm(current => ({ ...current, removeAvailability: true }))}>Remove</button>
          </div>

          <form onSubmit={submit}>
            <label>
              <span>Start date</span>
              <input type="date" value={form.startDate} onChange={event => setForm(current => ({ ...current, startDate: event.target.value }))} required />
            </label>
            <label>
              <span>End date</span>
              <input type="date" value={form.endDate} min={form.startDate || undefined} onChange={event => setForm(current => ({ ...current, endDate: event.target.value }))} />
              <small>Leave blank for a single day.</small>
            </label>

            <div className={`my-availability-confirm ${form.removeAvailability ? 'is-remove' : ''}`}>
              <strong>{form.removeAvailability ? 'Remove' : 'Add'} for:</strong>
              <span>{form.startDate ? formatRange(form.startDate, form.endDate || form.startDate) : 'Choose dates above'}</span>
              <small>Submission identity: {calendar.matchedName || memberName}</small>
            </div>

            <button type="submit" className={`my-availability-submit ${form.removeAvailability ? 'is-remove' : ''}`} disabled={submitState.status === 'saving' || !memberName}>
              {submitState.status === 'saving' ? 'Submitting…' : form.removeAvailability ? 'Confirm removal' : 'Confirm availability'}
            </button>
          </form>

          <button type="button" className="my-availability-refresh" onClick={() => setRefreshKey(value => value + 1)} disabled={calendar.status === 'loading'}>
            {calendar.status === 'loading' ? 'Refreshing calendar…' : 'Refresh calendar'}
          </button>
          <small className="my-availability-sync-note">Your existing Google Sheet automation remains the source of truth. A new submission may take a few minutes to appear here.</small>
        </section>
      </div>

      {calendar.status === 'ready' && (
        <section className="my-availability-upcoming">
          <div>
            <span className="availability-kicker">UPCOMING</span>
            <h3>{availableDates.filter(key => key >= todayKey).length} available days currently shown</h3>
          </div>
          <div className="my-availability-upcoming-days">
            {availableDates.filter(key => key >= todayKey).slice(0, 14).map(key => (
              <span key={key}>{new Date(`${key}T12:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>
            ))}
            {!availableDates.some(key => key >= todayKey) && <small>No upcoming available dates are currently marked on the program calendar.</small>}
          </div>
        </section>
      )}
    </div>
  );
}
